import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import PlanLimitsCard, { PlanLimitBanner } from '../components/PlanLimitsCard';
import UsageWidget from '../components/UsageWidget';
import { CaseStatusChart, BillableHoursChart, RevenueTrendChart } from '../components/DashboardCharts';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  FolderOpen,
  Clock,
  DollarSign,
  CheckSquare,
  Plus,
  ArrowRight,
  Building2,
  Shield
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function DashboardPage() {
  const { user, createTenant, getAuthHeaders } = useAuth();
  const { t, language } = useLanguage();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showTenantDialog, setShowTenantDialog] = useState(false);
  const [tenantName, setTenantName] = useState('');
  const [creatingTenant, setCreatingTenant] = useState(false);

  useEffect(() => {
    fetchStats();
  }, [user?.tenant_id]);

  const fetchStats = async () => {
    try {
      const response = await fetch(`${API}/dashboard/stats`, {
        credentials: 'include',
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setStats(data);
      }
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTenant = async (e) => {
    e.preventDefault();
    if (!tenantName.trim()) return;

    setCreatingTenant(true);
    try {
      await createTenant(tenantName.trim());
      toast.success(language === 'en' ? 'Organization created successfully!' : '¡Organización creada exitosamente!');
      setShowTenantDialog(false);
      fetchStats();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setCreatingTenant(false);
    }
  };

  // Show setup prompt if no tenant
  if (!user?.tenant_id && !loading) {
    return (
      <div className="p-6 lg:p-8" data-testid="dashboard-page">
        <div className="max-w-2xl mx-auto mt-20">
          <Card className="border-2 border-dashed">
            <CardContent className="p-12 text-center space-y-6">
              <div className="w-16 h-16 bg-bronze/10 rounded-full flex items-center justify-center mx-auto">
                <Building2 className="w-8 h-8 text-bronze" />
              </div>
              <div className="space-y-2">
                <h2 className="text-2xl font-serif font-bold text-navy">{t('dashboard.welcomeToLegalSuite')}</h2>
                <p className="text-muted-foreground max-w-md mx-auto">
                  {t('dashboard.toGetStarted')}
                </p>
              </div>
              <Dialog open={showTenantDialog} onOpenChange={setShowTenantDialog}>
                <DialogTrigger asChild>
                  <Button className="btn-accent" data-testid="create-org-btn">
                    <Plus className="w-4 h-4 mr-2" />
                    {t('dashboard.createOrganization')}
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle className="font-serif">{t('dashboard.createYourOrg')}</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleCreateTenant} className="space-y-4 pt-4">
                    <div className="space-y-2">
                      <Label htmlFor="tenantName">{t('auth.lawFirmName')}</Label>
                      <Input
                        id="tenantName"
                        data-testid="tenant-name-input"
                        placeholder="Smith & Associates LLP"
                        value={tenantName}
                        onChange={(e) => setTenantName(e.target.value)}
                        className="rounded-none"
                        required
                      />
                    </div>
                    <Button
                      type="submit"
                      data-testid="submit-tenant-btn"
                      className="w-full btn-primary"
                      disabled={creatingTenant}
                    >
                      {creatingTenant ? t('common.pleaseWait') : t('dashboard.createOrganization')}
                    </Button>
                  </form>
                </DialogContent>
              </Dialog>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  const statCards = [
    {
      title: t('dashboard.activeCases'),
      value: stats?.cases?.open || 0,
      total: stats?.cases?.total || 0,
      icon: FolderOpen,
      color: 'bg-navy',
      link: '/dashboard/cases'
    },
    {
      title: t('dashboard.billableHours'),
      value: stats?.time?.billable_hours || 0,
      subtitle: `$${(stats?.time?.total_amount || 0).toLocaleString()}`,
      icon: Clock,
      color: 'bg-bronze',
      link: '/dashboard/time'
    },
    {
      title: t('dashboard.revenueMTD'),
      value: `$${(stats?.invoices?.revenue || 0).toLocaleString()}`,
      subtitle: `${stats?.invoices?.paid || 0} ${t('dashboard.paid')}`,
      icon: DollarSign,
      color: 'bg-emerald',
      link: '/dashboard/billing'
    },
    {
      title: t('dashboard.pendingTasks'),
      value: (stats?.tasks?.todo || 0) + (stats?.tasks?.in_progress || 0),
      total: stats?.tasks?.total || 0,
      icon: CheckSquare,
      color: 'bg-bronze',
      link: '/dashboard/tasks'
    },
    {
      title: t('dashboard.trustBalance'),
      value: `$${(stats?.trust?.total_balance || 0).toLocaleString()}`,
      icon: Shield,
      color: 'bg-navy',
      link: '/dashboard/trust'
    }
  ];

  return (
    <div className="p-6 lg:p-8 space-y-8" data-testid="dashboard-page">
      {/* Plan limit banner - only shows when a limit is reached */}
      <PlanLimitBanner />

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl lg:text-3xl font-serif font-bold text-navy dark:text-foreground">
            {t('dashboard.welcomeBack')}, {user?.name?.split(' ')[0]}
          </h1>
          <p className="text-muted-foreground mt-1">{t('dashboard.hereIsWhatsHappening')}</p>
        </div>
        <Button
          onClick={() => navigate('/dashboard/cases')}
          className="btn-accent"
          data-testid="new-case-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          {t('dashboard.newCase')}
        </Button>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
        {statCards.map((stat, idx) => (
          <Card
            key={idx}
            className="card-base card-hover cursor-pointer"
            onClick={() => navigate(stat.link)}
            data-testid={`stat-card-${idx}`}
          >
            <CardContent className="p-6">
              <div className="flex items-start justify-between">
                <div className={`w-10 h-10 ${stat.color} rounded flex items-center justify-center`}>
                  <stat.icon className="w-5 h-5 text-white" />
                </div>
                <ArrowRight className="w-4 h-4 text-muted-foreground" />
              </div>
              <div className="mt-4">
                <p className="text-2xl font-bold text-foreground">{stat.value}</p>
                {stat.total !== undefined && (
                  <p className="text-sm text-muted-foreground">
                    {t('dashboard.ofTotal').replace('{total}', stat.total)}
                  </p>
                )}
                {stat.subtitle && (
                  <p className="text-sm text-muted-foreground">{stat.subtitle}</p>
                )}
                <p className="text-sm font-medium text-muted-foreground mt-1">{stat.title}</p>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* AI + storage consumption with low-capacity alert */}
      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          <UsageWidget />
        </div>
        <PlanLimitsCard />
      </div>

      {/* Charts row — at-a-glance insights using existing stats */}
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        <RevenueTrendChart months={6} />
        <CaseStatusChart cases={stats?.cases} />
        <BillableHoursChart time={stats?.time} />
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Recent Cases */}
        <Card className="lg:col-span-2 card-base">
          <CardHeader className="flex flex-row items-center justify-between pb-4">
            <CardTitle className="text-lg font-serif">{t('dashboard.quickActions')}</CardTitle>
          </CardHeader>
          <CardContent className="grid sm:grid-cols-2 gap-4">
            <Button
              variant="outline"
              className="h-auto p-4 justify-start gap-4 rounded-none"
              onClick={() => navigate('/dashboard/time')}
              data-testid="quick-action-time"
            >
              <Clock className="w-8 h-8 text-bronze" />
              <div className="text-left">
                <p className="font-medium">{t('dashboard.startTimer')}</p>
                <p className="text-sm text-muted-foreground">{t('dashboard.trackBillable')}</p>
              </div>
            </Button>
            <Button
              variant="outline"
              className="h-auto p-4 justify-start gap-4 rounded-none"
              onClick={() => navigate('/dashboard/documents')}
              data-testid="quick-action-docs"
            >
              <FolderOpen className="w-8 h-8 text-navy" />
              <div className="text-left">
                <p className="font-medium">{t('dashboard.addDocument')}</p>
                <p className="text-sm text-muted-foreground">{t('dashboard.uploadFiles')}</p>
              </div>
            </Button>
            <Button
              variant="outline"
              className="h-auto p-4 justify-start gap-4 rounded-none"
              onClick={() => navigate('/dashboard/billing')}
              data-testid="quick-action-invoice"
            >
              <DollarSign className="w-8 h-8 text-emerald" />
              <div className="text-left">
                <p className="font-medium">{t('dashboard.createInvoice')}</p>
                <p className="text-sm text-muted-foreground">{t('dashboard.billClients')}</p>
              </div>
            </Button>
            <Button
              variant="outline"
              className="h-auto p-4 justify-start gap-4 rounded-none"
              onClick={() => navigate('/dashboard/tasks')}
              data-testid="quick-action-task"
            >
              <CheckSquare className="w-8 h-8 text-bronze" />
              <div className="text-left">
                <p className="font-medium">{t('dashboard.addTask')}</p>
                <p className="text-sm text-muted-foreground">{t('dashboard.trackTodo')}</p>
              </div>
            </Button>
          </CardContent>
        </Card>

        {/* Overview Stats */}
        <Card className="card-base">
          <CardHeader className="pb-4">
            <CardTitle className="text-lg font-serif">{t('dashboard.thisMonth')}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-3 h-3 bg-emerald rounded-full" />
                <span className="text-sm">{t('dashboard.closedCases')}</span>
              </div>
              <span className="font-bold">{stats?.cases?.closed || 0}</span>
            </div>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-3 h-3 bg-bronze rounded-full" />
                <span className="text-sm">{t('dashboard.totalHours')}</span>
              </div>
              <span className="font-bold">{stats?.time?.total_hours || 0}h</span>
            </div>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-3 h-3 bg-navy rounded-full" />
                <span className="text-sm">{t('dashboard.draftInvoices')}</span>
              </div>
              <span className="font-bold">{stats?.invoices?.draft || 0}</span>
            </div>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-3 h-3 bg-destructive rounded-full" />
                <span className="text-sm">{t('dashboard.pendingInvoices')}</span>
              </div>
              <span className="font-bold">{stats?.invoices?.sent || 0}</span>
            </div>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-3 h-3 bg-emerald rounded-full" />
                <span className="text-sm">{t('dashboard.completedTasks')}</span>
              </div>
              <span className="font-bold">{stats?.tasks?.done || 0}</span>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
